import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, Package, DollarSign, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  const initialForm = {
    name: '',
    description: '',
    price: '',
    category: 't-shirt',
    imageUrl: '',
    variants: [{ size: 'M', color: 'Black', inventory: 10 }]
  };
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop/admin/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        images: formData.imageUrl ? [formData.imageUrl] : [],
        variants: formData.variants.map(v => ({
          size: v.size,
          color: v.color,
          inventory: parseInt(v.inventory) || 0
        }))
      };

      if (editingProduct) {
        await axios.put(`${API_URL}/shop/admin/products/${editingProduct.id}`, payload);
        toast.success('Product updated!');
      } else {
        await axios.post(`${API_URL}/shop/admin/products`, payload);
        toast.success('Product created!');
      }

      setShowForm(false);
      setEditingProduct(null);
      setFormData(initialForm);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      price: product.price.toString(),
      category: product.category,
      imageUrl: product.images?.[0] || '',
      variants: product.variants?.length 
        ? product.variants 
        : [{ size: 'M', color: 'Black', inventory: 10 }]
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/shop/admin/products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, { size: 'M', color: 'Black', inventory: 10 }]
    }));
  };

  const removeVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const updateVariant = (index, field, value) => {
    setFormData(prev => {
      const newVariants = [...prev.variants];
      newVariants[index] = { ...newVariants[index], [field]: value };
      return { ...prev, variants: newVariants };
    });
  };

  const categories = ['t-shirt', 'hoodie', 'hat'];
  const sizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', 'One Size'];
  const colors = ['Black', 'White', 'Red', 'Blue', 'Navy', 'Gray'];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Products</h1>
        <Button
          onClick={() => { setEditingProduct(null); setFormData(initialForm); setShowForm(true); }}
          className="bg-[#D9F99D] text-black hover:bg-[#BEF264]"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="glass-card rounded-xl p-4">
          <Package className="w-5 h-5 text-[#D9F99D] mb-2" />
          <p className="text-2xl font-bold">{products.filter(p => p.active).length}</p>
          <p className="text-zinc-400 text-sm">Active Products</p>
        </div>
        <div className="glass-card rounded-xl p-4">
          <DollarSign className="w-5 h-5 text-green-400 mb-2" />
          <p className="text-2xl font-bold">
            ${products.reduce((sum, p) => sum + (p.active ? p.price : 0), 0).toFixed(0)}
          </p>
          <p className="text-zinc-400 text-sm">Total Value</p>
        </div>
      </div>

      {/* Products List */}
      <div className="glass-card rounded-2xl p-6">
        <h2 className="text-xl font-bold mb-4">All Products</h2>
        {loading ? (
          <div className="text-center py-8 text-zinc-400">Loading...</div>
        ) : products.length > 0 ? (
          <div className="space-y-4">
            {products.map((product) => (
              <div 
                key={product.id} 
                className={`flex items-center gap-4 p-4 rounded-xl ${product.active !== false ? 'bg-white/5' : 'bg-red-500/10 opacity-60'}`}
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-zinc-900 flex-shrink-0">
                  {product.images?.[0] ? (
                    <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-6 h-6 text-zinc-700" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-bold">{product.name}</h3>
                  <p className="text-sm text-zinc-400 capitalize">{product.category}</p>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="text-[#D9F99D] font-bold">${product.price?.toFixed(2)}</span>
                    <span className="text-xs text-zinc-500">{product.variants?.length || 0} variants</span>
                    {product.active === false && <span className="text-xs text-red-400">Inactive</span>}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(product)} className="text-zinc-400 hover:text-white">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(product.id)} className="text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Package className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2">No products yet</h3>
            <p className="text-zinc-400">Add your first product to get started</p>
          </div>
        )}
      </div>

      {/* Add/Edit Product Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#18181B] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Product Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="DJ Shadow T-Shirt"
                  className="bg-black/20 border-white/10"
                  data-testid="product-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Price (USD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="29.99"
                  className="bg-black/20 border-white/10"
                  data-testid="product-price"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <div className="flex gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, category: cat }))}
                    className={`px-4 py-2 rounded-lg border capitalize ${
                      formData.category === cat
                        ? 'border-[#D9F99D] bg-[#D9F99D]/10 text-[#D9F99D]'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Product description..."
                className="bg-black/20 border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label>Product Image URL</Label>
              <Input
                value={formData.imageUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
                placeholder="https://example.com/image.jpg"
                className="bg-black/20 border-white/10"
              />
            </div>

            {/* Variants */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Variants</Label>
                <Button type="button" variant="outline" size="sm" onClick={addVariant} className="bg-white/5 border-white/10">
                  <Plus className="w-3 h-3 mr-1" /> Add Variant
                </Button>
              </div>

              {formData.variants.map((variant, index) => (
                <div key={index} className="flex items-end gap-3 p-3 bg-white/5 rounded-lg">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Size</Label>
                    <select
                      value={variant.size}
                      onChange={(e) => updateVariant(index, 'size', e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                    >
                      {sizes.map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Color</Label>
                    <select
                      value={variant.color}
                      onChange={(e) => updateVariant(index, 'color', e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                    >
                      {colors.map((color) => (
                        <option key={color} value={color}>{color}</option>
                      ))}
                    </select>
                  </div>
                  <div className="w-24 space-y-1">
                    <Label className="text-xs">Stock</Label>
                    <Input
                      type="number"
                      value={variant.inventory}
                      onChange={(e) => updateVariant(index, 'inventory', e.target.value)}
                      className="bg-black/40 border-white/10"
                    />
                  </div>
                  {formData.variants.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeVariant(index)} className="text-red-400 hover:bg-red-500/10">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={saving} className="flex-1 bg-[#D9F99D] text-black hover:bg-[#BEF264]" data-testid="save-product-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : (editingProduct ? 'Update Product' : 'Create Product')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="bg-white/5 border-white/10">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
